import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Plus, Search, Upload, Edit, Trash2, Mail, Phone, User, UserPlus, Users, Star, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { HeroQuickMenuCard } from '@/components/shared/HeroQuickMenuCard';
import { ImportContactsModal } from '@/components/ImportContactsModal';
import { userContactService } from '@/lib/api/user-contact.service';
import { apiClient } from '@/lib/api/client';
import { useAuth } from '@/hooks/useAuth';
import type { UserContact, CreateUserContactRequest, UpdateUserContactRequest } from '@/types/user-contact.types';

export const ContactsManagement: React.FC = () => {
  const { t } = useTranslation(['contacts', 'common']);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [contacts, setContacts] = useState<UserContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingContactId, setEditingContactId] = useState<number | null>(null);
  const [editFormData, setEditFormData] = useState<UpdateUserContactRequest>({});
  const [isSavingContact, setIsSavingContact] = useState(false);
  const [selectedContact, setSelectedContact] = useState<UserContact | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [loadingContactsRequest, setLoadingContactsRequest] = useState<Promise<void> | null>(null);
  
  const [formData, setFormData] = useState<CreateUserContactRequest>({
    full_name: '',
    email: '',
    phone: '',
    relationship: '',
    is_primary: false,
    status: 'active'
  });

  useEffect(() => {
    if (user?.id) {
      loadContacts();
    } else {
      // Clear contacts when user logs out
      setContacts([]);
    }
  }, [user?.id]);

  const loadContacts = async (bustCache = false) => {
    // Prevent multiple simultaneous requests
    if (loadingContactsRequest && !bustCache) {
      return loadingContactsRequest;
    }

    const requestPromise = (async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Add cache-busting parameter if requested
        const url = bustCache ? `/user-contacts?_t=${Date.now()}` : '/user-contacts';
        const response = await apiClient.get(url);
        
        const newContacts = response.data.data || [];

        
        setContacts(newContacts);
      } catch (err: any) {
        console.error('❌ Failed to load contacts:', err);
        setError('Failed to load contacts. Please try again.');
      } finally {
        setLoading(false);
        setLoadingContactsRequest(null);
      }
    })();

    setLoadingContactsRequest(requestPromise);
    return requestPromise;
  };

  const handleAddContact = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.full_name.trim()) {
      setError('Full name is required');
      return;
    }

    try {
      setError(null);
      setLoading(true);
      
      await userContactService.createContact(formData);
      await loadContacts();
      
      resetForm();
    } catch (err: any) {
      console.error('Error saving contact:', err);
      setError(err.response?.data?.message || 'Failed to save contact. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditContact = (contact: UserContact) => {
    setError(null);
    setEditingContactId(contact.id);
    setEditFormData({
      full_name: contact.full_name,
      email: contact.email || '',
      phone: contact.phone || '',
      relationship: contact.relationship || '',
      is_primary: contact.is_primary,
      status: contact.status
    });
  };

  const handleCancelEdit = () => {
    setEditingContactId(null);
    setEditFormData({});
    setError(null);
  };

  const handleUpdateContact = async (event: React.FormEvent) => {
    event.preventDefault();

    if (editingContactId === null || !editFormData.full_name?.trim()) {
      setError('Full name is required');
      return;
    }

    try {
      setError(null);
      setIsSavingContact(true);
      const updatedContact = await userContactService.updateContact(editingContactId, editFormData);
      setContacts((currentContacts) => currentContacts.map((contact) =>
        contact.id === updatedContact.id ? updatedContact : contact
      ));
      setSelectedContact((currentContact) =>
        currentContact?.id === updatedContact.id ? updatedContact : currentContact
      );
      handleCancelEdit();
    } catch (err: any) {
      console.error('Error updating contact:', err);
      setError(err.response?.data?.message || 'Failed to save contact. Please try again.');
    } finally {
      setIsSavingContact(false);
    }
  };

  const handleDeleteContact = async (contactId: number) => {
    if (!confirm('Are you sure you want to delete this contact?')) {
      return;
    }

    try {
      await userContactService.deleteContact(contactId);
      await loadContacts();
    } catch (err: any) {
      console.error('Error deleting contact:', err);
      setError('Failed to delete contact. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      full_name: '',
      email: '',
      phone: '',
      relationship: '',
      is_primary: false,
      status: 'active'
    });
    setShowAddForm(false);
  };

  const filteredContacts = contacts.filter(contact =>
    contact.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.phone?.includes(searchTerm) ||
    contact.relationship?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const primaryContactsCount = contacts.filter((contact) => contact.is_primary).length;
  const connectedContactsCount = contacts.filter((contact) => contact.user_id !== undefined).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <PageContainer width="wide" className="space-y-6">
      <AppBreadcrumbs
        items={[
          { label: t('common:nav.home'), to: '/home' },
          { label: t('contacts:title') },
        ]}
      />

      <HeroQuickMenuCard
        borderClassName="border-primary/20"
        heroClassName="bg-gradient-to-br from-primary/10 via-primary/[0.04] to-white"
        heroContent={
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary/10 ring-1 ring-primary/20 flex items-center justify-center flex-shrink-0">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-bold tracking-tight text-foreground">Contact Management</h1>
                <p className="mt-1 text-sm text-muted-foreground">Manage your contacts and import from CSV files.</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(-1)}
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>{t('common:actions.back')}</span>
              </Button>
              <Button
                onClick={() => setShowImportModal(true)}
                className="flex items-center space-x-2"
                variant="outline"
                data-testid="import-contacts-button"
              >
                <Upload className="h-4 w-4" />
                <span>{t('contacts:actions.importContacts')}</span>
              </Button>
              <Button
                onClick={() => setShowAddForm(true)}
                className="flex items-center space-x-2"
                data-testid="add-contact-button"
              >
                <Plus className="h-4 w-4" />
                <span>{t('contacts:actions.addContact')}</span>
              </Button>
            </div>
          </div>
        }
        columns={3}
        items={[
          {
            key: 'contacts',
            icon: Users,
            iconClassName: 'text-blue-600',
            hoverClassName: 'hover:bg-blue-50',
            label: t('contacts:title'),
            value: contacts.length,
          },
          {
            key: 'connected',
            icon: UserPlus,
            iconClassName: 'text-cyan-600',
            hoverClassName: 'hover:bg-cyan-50',
            label: 'Connected',
            value: connectedContactsCount,
          },
          {
            key: 'primary',
            icon: Star,
            iconClassName: 'text-amber-600',
            hoverClassName: 'hover:bg-amber-50',
            label: 'Primary',
            value: primaryContactsCount,
          },
        ]}
      />

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {/* Add/Edit Contact Form */}
      {showAddForm && (
        <Card data-testid="contact-form">
          <CardHeader>
            <CardTitle>Add New Contact</CardTitle>
            <CardDescription>
              Enter contact details to add a new contact
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAddContact} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="full_name">Full Name *</Label>
                  <Input
                    id="full_name"
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    placeholder="Enter full name"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Enter email address"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="Enter phone number"
                  />
                </div>
                <div>
                  <Label htmlFor="relationship">Relationship</Label>
                  <Input
                    id="relationship"
                    type="text"
                    value={formData.relationship}
                    onChange={(e) => setFormData({ ...formData, relationship: e.target.value })}
                    placeholder="e.g., Friend, Family, Colleague"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between pt-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="is_primary"
                    checked={formData.is_primary}
                    onChange={(e) => setFormData({ ...formData, is_primary: e.target.checked })}
                    aria-label="Mark as primary contact"
                    title="Mark as primary contact"
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor="is_primary">Primary contact</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Button type="button" variant="outline" onClick={resetForm} disabled={loading}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading}>
                    {loading ? 'Saving...' : 'Add Contact'}
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Contacts List */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle>{t('contacts:title')}</CardTitle>
              <CardDescription>
                Your contact list with import and management capabilities
              </CardDescription>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-4">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                type="text"
                placeholder="Search contacts by name, email, phone, or relationship..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="contact-search-input"
              />
              {searchTerm && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2"
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear contact search"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            <span className="flex-shrink-0 text-sm text-gray-500" aria-live="polite">
              {filteredContacts.length} {filteredContacts.length === 1 ? 'contact' : 'contacts'}
            </span>
          </div>
        </CardHeader>
        <CardContent>
          {filteredContacts.length === 0 ? (
            <div className="text-center py-12">
              <User className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No contacts found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm ? 'Try adjusting your search terms.' : 'Get started by adding a contact or importing from CSV.'}
              </p>
              {!searchTerm && (
                <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                  <Button onClick={() => setShowAddForm(true)} className="flex items-center space-x-2">
                    <Plus className="h-4 w-4" />
                    <span>{t('contacts:actions.addContact')}</span>
                  </Button>
                  <Button onClick={() => setShowImportModal(true)} variant="outline" className="flex items-center space-x-2">
                    <Upload className="h-4 w-4" />
                    <span>Import CSV</span>
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="grid gap-4" data-testid="contacts-list">
              {filteredContacts.map((contact) => {
                const isEditing = editingContactId === contact.id;

                return (
                <div
                  key={contact.id}
                  className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                  data-testid={`contact-card-${contact.id}`}
                >
                  {isEditing ? (
                    <form onSubmit={handleUpdateContact} className="space-y-4">
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                          <Label htmlFor={`contact-${contact.id}-full-name`}>Full name *</Label>
                          <Input id={`contact-${contact.id}-full-name`} value={editFormData.full_name ?? ''} onChange={(event) => setEditFormData((current) => ({ ...current, full_name: event.target.value }))} required />
                        </div>
                        <div>
                          <Label htmlFor={`contact-${contact.id}-email`}>Email</Label>
                          <Input id={`contact-${contact.id}-email`} type="email" value={editFormData.email ?? ''} onChange={(event) => setEditFormData((current) => ({ ...current, email: event.target.value }))} />
                        </div>
                        <div>
                          <Label htmlFor={`contact-${contact.id}-phone`}>Phone</Label>
                          <Input id={`contact-${contact.id}-phone`} type="tel" value={editFormData.phone ?? ''} onChange={(event) => setEditFormData((current) => ({ ...current, phone: event.target.value }))} />
                        </div>
                        <div>
                          <Label htmlFor={`contact-${contact.id}-relationship`}>Relationship</Label>
                          <Input id={`contact-${contact.id}-relationship`} value={editFormData.relationship ?? ''} onChange={(event) => setEditFormData((current) => ({ ...current, relationship: event.target.value }))} />
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <label className="flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={editFormData.is_primary ?? false} onChange={(event) => setEditFormData((current) => ({ ...current, is_primary: event.target.checked }))} />
                          Primary contact
                        </label>
                        <div className="flex items-center gap-2">
                          <Button type="button" variant="outline" onClick={handleCancelEdit} disabled={isSavingContact}>Cancel</Button>
                          <Button type="submit" disabled={isSavingContact}>{isSavingContact ? 'Saving...' : 'Save'}</Button>
                        </div>
                      </div>
                    </form>
                  ) : (
                  <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium ${
                      contact.is_primary ? 'bg-blue-600' : 'bg-gray-500'
                    }`}>
                      {contact.full_name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 flex items-center space-x-2">
                        <span 
                          className="cursor-pointer hover:text-blue-600" 
                          data-testid={`contact-name-${contact.id}`}
                          onClick={() => {
                            setSelectedContact(contact);
                            setShowDetailsModal(true);
                          }}
                        >
                          {contact.full_name}
                        </span>
                        {contact.is_primary && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                            Primary
                          </span>
                        )}
                      </h3>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        {contact.email && (
                          <div className="flex items-center space-x-1">
                            <Mail className="h-3 w-3" />
                            <span>{contact.email}</span>
                          </div>
                        )}
                        {contact.phone && (
                          <div className="flex items-center space-x-1">
                            <Phone className="h-3 w-3" />
                            <span>{contact.phone}</span>
                          </div>
                        )}
                        {contact.relationship && (
                          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                            {contact.relationship}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditContact(contact)}
                      className="flex items-center space-x-1"
                      aria-label={`Edit ${contact.full_name}`}
                    >
                      <Edit className="h-3 w-3" />
                      <span>Edit</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteContact(contact.id)}
                      className="flex items-center space-x-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-3 w-3" />
                      <span>Delete</span>
                    </Button>
                  </div>
                </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Import Contacts Modal */}
      <ImportContactsModal
        isOpen={showImportModal}
        celebrationId={undefined}
        onClose={() => setShowImportModal(false)}
        onSuccess={() => {
          setShowImportModal(false);
          loadContacts();
        }}
      />
      
      {/* Contact Details Modal */}
      {showDetailsModal && selectedContact && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          data-testid="contact-details"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowDetailsModal(false);
              setSelectedContact(null);
            }
          }}
        >
          <Card className="w-full max-w-md contact-modal" role="dialog">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Contact Details</CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedContact(null);
                  }}
                >
                  ×
                </Button>
              </div>
            </CardHeader>
            <CardContent data-testid="contact-info">
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center text-white font-medium text-xl ${
                    selectedContact.is_primary ? 'bg-blue-600' : 'bg-gray-500'
                  }`}>
                    {selectedContact.full_name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      {selectedContact.full_name}
                    </h3>
                    {selectedContact.is_primary && (
                      <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                        Primary Contact
                      </span>
                    )}
                  </div>
                </div>
                
                {selectedContact.email && (
                  <div>
                    <h4 className="font-medium text-gray-900">Email</h4>
                    <div className="flex items-center space-x-2 text-gray-600">
                      <Mail className="h-4 w-4" />
                      <span>{selectedContact.email}</span>
                    </div>
                  </div>
                )}
                
                {selectedContact.phone && (
                  <div>
                    <h4 className="font-medium text-gray-900">Phone</h4>
                    <div className="flex items-center space-x-2 text-gray-600">
                      <Phone className="h-4 w-4" />
                      <span>{selectedContact.phone}</span>
                    </div>
                  </div>
                )}
                
                {selectedContact.relationship && (
                  <div>
                    <h4 className="font-medium text-gray-900">Relationship</h4>
                    <span className="text-gray-600">{selectedContact.relationship}</span>
                  </div>
                )}
                
                <div>
                  <h4 className="font-medium text-gray-900">Status</h4>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    selectedContact.status === 'active' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedContact.status}
                  </span>
                </div>
                
                <div className="flex gap-2 pt-4">
                  <Button 
                    onClick={() => {
                      setShowDetailsModal(false);
                      handleEditContact(selectedContact);
                    }}
                    className="flex-1"
                  >
                    Edit Contact
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setShowDetailsModal(false);
                      setSelectedContact(null);
                    }}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </PageContainer>
  );
};
